import type { BoxProps } from '@chakra-ui/react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { AdvancedImage } from '@cloudinary/react';
import {
  CarouselContent,
  CarouselProvider,
} from '@time-line/components/common';
import { Star, ThumbsUp } from '@time-line/components/icons';
import {
  CUSTOMER_COUNT,
  CUSTOMERS_STACK_IMAGE,
} from 'components/common/CustomerResults';
import { useRatingsOverview } from 'hooks/reviews/useRatingsOverview';
import type { ReactNode } from 'react';
import { A11y, Autoplay, EffectFade } from 'swiper/modules';

const AUTOPLAY_DELAY = 1500;
const FADE_SPEED = 600;

function TrustPoint({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <HStack spacing="4xs" justifyContent="center" height="xs">
      {icon}
      <Text size="detail" fontWeight="medium" whiteSpace="nowrap">
        {text}
      </Text>
    </HStack>
  );
}

type CartTrustPointsProps = BoxProps & {
  isAutoplayEnabled?: boolean;
};

export function CartTrustPoints({
  isAutoplayEnabled = true,
  sx,
  ...props
}: CartTrustPointsProps) {
  const { data: overview } = useRatingsOverview();

  const trustPoints = [
    overview?.ratingAverage != null && overview.ratingCount != null && (
      <TrustPoint
        key="reviews"
        icon={<Star boxSize="2xs" />}
        text={`${overview.ratingAverage.toFixed(1)} based on ${overview.ratingCount.toLocaleString('en-US')} reviews`}
      />
    ),
    overview?.recommendedPercentage != null && (
      <TrustPoint
        key="recommended"
        icon={<ThumbsUp boxSize="2xs" />}
        text={`${Math.round(overview.recommendedPercentage)}% recommend our products`}
      />
    ),
    <TrustPoint
      key="customers"
      icon={
        <AdvancedImage
          cldImg={CUSTOMERS_STACK_IMAGE}
          alt=""
          width={40}
          height={17}
          loading="lazy"
        />
      }
      text={`${CUSTOMER_COUNT} customers seeing real results`}
    />,
  ].filter(Boolean);

  const hasMultipleSlides = trustPoints.length > 1;
  const shouldAutoplay = hasMultipleSlides && isAutoplayEnabled;

  return (
    <Box
      {...props}
      sx={{
        ...sx,
        '& .swiper-slide:not(.swiper-slide-active)': {
          transitionTimingFunction: 'linear(0, 1 50%, 1)',
        },
        '& .swiper-slide-active': {
          transitionTimingFunction: 'linear(0, 0 50%, 1)',
        },
      }}
    >
      <CarouselProvider>
        <CarouselContent
          key={trustPoints.length}
          modules={[A11y, Autoplay, EffectFade]}
          slidesPerView={1}
          speed={FADE_SPEED}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          loop={hasMultipleSlides}
          autoplay={
            shouldAutoplay
              ? {
                  delay: AUTOPLAY_DELAY,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
        >
          {trustPoints}
        </CarouselContent>
      </CarouselProvider>
    </Box>
  );
}
